/**
 * AppProviders — single entry point that wraps the app with every
 * top-level provider.
 *
 * Provider order (outer → inner):
 *   Redux Provider           (store: must be outermost — the rest
 *                            read from it, including V2ThemeProvider)
 *     CssBaseline
 *     StyledEngineProvider   (MUI v5+ styles injection priority)
 *       MuiThemeBridge       (legacy theme-v9, rebuilt per colour mode)
 *         ErrorBoundary      (outer: catches the providers themselves)
 *           V2ThemeProvider  (reads theme from redux, mirrors to DOM)
 *             I18nextProvider
 *               NotificationsProvider (notistack)
 *                 ClientProvider (react-fetching-library)
 *                   BrowserRouter (with legacy history)
 *                     NavigationInitializer (hydrates routes in redux)
 *                       UserProvider (auth + metadata)
 *                         CacheBuster (version check, auto-refresh)
 *                           ErrorBoundary (inner: page-level recovery)
 *                             children (the App component / router)
 *
 * The outer ErrorBoundary is what keeps a crash inside a provider —
 * notistack rendering a snackbar, say — from unmounting the whole
 * tree and leaving the user on a blank page with nothing to click.
 */
import { useMemo } from 'react';
import { CssBaseline } from 'mui/core';
import { ThemeProvider as MuiThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { BrowserRouter } from 'react-router';
import { I18nextProvider } from 'react-i18next';
import { Provider as ReduxProvider, useSelector } from 'react-redux';
import { createAppTheme } from '@shared/config/theme';
import i18n from '@shared/config/i18n';
import history from '@shared/lib/history';
import store from '@app/store';
import NavigationInitializer from 'redux_store/components/NavigationInitializer';
import { ThemeProvider as V2ThemeProvider } from '@shared/ui/tokens';
import { ErrorBoundary } from '@shared/ui';
import NotificationsProvider from './NotificationsProvider';
import ClientProvider from './ClientProvider';
import UserProvider from './UserProvider';
import CacheBuster from '../../CacheBuster';

/**
 * Rebuilds the MUI theme whenever the user's colour mode changes, so
 * MUI's own surfaces follow the theme. It reads the same redux value
 * `V2ThemeProvider` mirrors to `<html data-theme>`, and has to sit
 * inside the redux Provider to do so.
 */
const MuiThemeBridge = ({ children }) => {
  const mode = useSelector(state => state.appSettings.theme);
  const theme = useMemo(() => createAppTheme(mode), [mode]);
  return <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>;
};

const AppProviders = ({ children }) => (
  <ReduxProvider store={store}>
    <CssBaseline />
    <StyledEngineProvider injectFirst>
      <MuiThemeBridge>
        <ErrorBoundary>
          <V2ThemeProvider>
            <I18nextProvider i18n={i18n}>
              <NotificationsProvider>
                <ClientProvider>
                  <BrowserRouter history={history}>
                    <NavigationInitializer>
                      <UserProvider>
                        <CacheBuster>
                          <ErrorBoundary>{children}</ErrorBoundary>
                        </CacheBuster>
                      </UserProvider>
                    </NavigationInitializer>
                  </BrowserRouter>
                </ClientProvider>
              </NotificationsProvider>
            </I18nextProvider>
          </V2ThemeProvider>
        </ErrorBoundary>
      </MuiThemeBridge>
    </StyledEngineProvider>
  </ReduxProvider>
);

export default AppProviders;
